import React from 'react';
import { useOrder } from '../state/OrderContext';
import { TRIP_PICKED_UP, TRIP_SHOPPING, isTripFor } from '../lib/pickupTrip';
import { CheckIcon, ExternalLinkIcon, UndoIcon } from './icons';
import './PickupSection.css';

// Getting the list to the shop.
//
// The whole scope of this section, stated once so it cannot drift: it opens
// the retailer's own website in a new tab. **It does not put the ingredients
// in anybody's basket.** No cart is created, nothing is sent anywhere, and no
// order exists at any point. Transferring a list into a retailer's cart needs
// a supported per-retailer integration, which this release does not have — so
// the user carries the list across themselves, and "Copy list" on the shopping
// list above is how.
//
// That is also why "I've picked this up" is a button rather than something we
// work out. Opening a tab tells us a tab was opened. Everything after that
// happens on someone else's website, and guessing at it would produce a
// confident record of a pickup that may never have occurred — which the user
// only discovers at a counter, with no food.

export default function PickupSection() {
  const { store, trip, beginPickup, markPickedUp, undoPickedUp } = useOrder();

  // No shop chosen means there is nothing to visit. The shopping list above is
  // untouched and still copyable; StorePicker has already said what happened.
  if (!store) return null;

  // A trip belonging to a store the user has since switched away from is not
  // this store's trip, and is not shown as one.
  const current = isTripFor(trip, store) ? trip : null;
  const started = current?.status === TRIP_SHOPPING;
  const pickedUp = current?.status === TRIP_PICKED_UP;

  return (
    <section className="card pickup">
      <p className="card__label">Shop this list</p>

      {store.website ? (
        <a
          className="button button--primary pickup-visit"
          href={store.website}
          target="_blank"
          rel="noreferrer"
          onClick={() => beginPickup(store)}
        >
          Visit {store.name} website <ExternalLinkIcon size={17} />
        </a>
      ) : (
        <p className="status">
          We don&rsquo;t have a website for {store.name}. Choose another shop above, or copy the list and
          take it with you.
        </p>
      )}

      {/* The instruction and the boundary, together, beside the button they
          are about — a disclaimer somewhere else on the page is not doing its
          job. Neither sentence claims this shop offers pickup: we have not
          checked, and the user is the one who finds out. */}
      <p className="pickup-note">Add items and check pickup availability on the store&rsquo;s website.</p>
      <p className="pickup-scope">
        Your list isn&rsquo;t sent to {store.name} — copy it from the shopping list above and add the
        items yourself.
      </p>

      {/* Only ever the user's own word for it. Nothing below is derived from
          the tab being opened, from time passing, or from coming back. */}
      {pickedUp ? (
        <p className="pickup-done">
          <CheckIcon size={16} /> You marked this picked up at {current.storeName ?? store.name}.
          <button type="button" className="link-button pickup-done__undo" onClick={undoPickedUp}>
            <UndoIcon size={14} /> Not yet
          </button>
        </p>
      ) : started ? (
        <button type="button" className="button button--secondary pickup-confirm" onClick={markPickedUp}>
          <CheckIcon size={16} /> I&rsquo;ve picked this up
        </button>
      ) : null}
    </section>
  );
}
